import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import CallUs from '../contact/CallUs';

export default function Property() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
      const loadService = async () => {
        try {
          setLoading(true);
          const res = await axios.get(`${process.env.REACT_APP_API_PATH}properties/public`, {
            params: { propertySlug: slug },
          });
          if (res.status !== +process.env.REACT_APP_RECORD_NOT_FOUND) {
            const { error, data } = res.data;
            if (!error && typeof data === 'object') {
              setService(data);
            }
          }
        } catch (err) {
          console.error(err);
        } finally { 
          setLoading(false);
        }
      };
    loadService();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="text-center text-gray-500 py-20 bg-gray-50">
        <p className="text-lg">Service not found or failed to load.</p>
      </div>
    );
  }

  const {
    rootImagePath,
    defaultImage,
    name,
    fullDescription,
    shortDescription,
    images,
    videos,
    service: relatedService,
  } = service;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-10 px-4">
      <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Title */}
        <div className="text-center px-8 py-10 border-b border-gray-200">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 text-center my-2">{name}</h1>
          {shortDescription && (
            <p className="text-lg text-gray-600">{shortDescription}</p>
          )}
        </div>

        {/* Centered Default Image */}
        <div className="flex justify-center items-center bg-gray-100 py-8 px-4">
          <img
            src={rootImagePath + defaultImage}
            alt={name}
            className="rounded-2xl shadow-xl max-h-[500px] w-auto max-w-full object-contain"
            onError={(e) => (e.target.src = '/favicon.ico')}
          />
        </div>

        {/* Image Gallery */}
        {images?.length > 0 && (
          <div className="px-8 py-6">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4 text-center my-2">Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {images.map((img, idx) => (
                <img
                  key={idx}
                  src={img.url}
                  alt={`Gallery IMG ${idx + 1}`}
                  className="rounded-xl shadow-md object-cover h-48 w-full hover:scale-105 transition-transform duration-300"
                  onError={(e) => (e.target.src = '/favicon.ic')}
                />
              ))}
            </div>
          </div>
        )}

        {/* Video Section */}
        {videos?.length > 0 && (
          <div className="px-8 py-8">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4 text-center my-2">Videos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videos.map((vid, idx) => (
                <video
                  key={idx}
                  controls
                  className="w-full rounded-2xl shadow-lg"
                  src={vid.url}
                >
                  Your browser does not support the video tag.
                </video>
              ))}
            </div>
          </div>
        )}

        {/* Full Description */}
        <div className="px-8 py-10 border-t border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center my-2">Details</h2>
          <div
            className="prose prose-lg max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: fullDescription }}
          />
        </div>

        {/* Related Service Info */}
        {relatedService && (
          <div className="px-8 py-8 border-t border-gray-100 bg-gray-50 rounded-b-3xl">
            <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center my-2">
              Service Category: {relatedService.name}
            </h3>
            <div
              className="prose max-w-none text-gray-600"
              dangerouslySetInnerHTML={{ __html: relatedService.fullDescription }}
            />
          </div>
        )}

        <CallUs />

      </div>
    </div>
  );
}